var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
import React, { useState, Fragment, useEffect } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Collapse, { VARIANT_BOLD } from './Collapse';
import cx from 'classnames';
var useStyles = makeStyles(function (theme) {
  return {
    container: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    title: _extends({
      fontWeight: 'bold'
    }, R.pathOr({}, ['mixins', 'collapsibleTree', 'title'], theme)),
    divider: {
      marginBottom: theme.spacing(2)
    },
    spacingValue: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    overflow: {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'break-word'
    }
  };
});
var CollapsibleTree = function CollapsibleTree(_ref) {
  var _ref$data = _ref.data,
    data = _ref$data === undefined ? [] : _ref$data,
    _ref$defaultDepthLeve = _ref.defaultDepthLevel,
    defaultDepthLevel = _ref$defaultDepthLeve === undefined ? 1 : _ref$defaultDepthLeve,
    isLoading = _ref.isLoading,
    children = _ref.children,
    labels = _ref.labels,
    _ref$testId = _ref.testId,
    testId = _ref$testId === undefined ? 'collapsible-tree' : _ref$testId,
    valueHandler = _ref.valueHandler,
    icon = _ref.icon,
    onClick = _ref.onClick;
  var classes = useStyles();
  var _useState = useState(defaultDepthLevel),
    depthLevel = _useState[0],
    setDepthLevel = _useState[1];
  useEffect(function () {
    return setDepthLevel(defaultDepthLevel);
  }, [defaultDepthLevel]);
  if (isLoading) {
    return React.createElement(Grid, {
      container: true,
      item: true,
      justifyContent: 'center'
    }, React.createElement(CircularProgress, null));
  }
  var handleCollapse = function handleCollapse(isAllOpen) {
    if (isAllOpen) return setDepthLevel(undefined);
    return setDepthLevel(defaultDepthLevel);
  };
  var getValue = function getValue(_ref2) {
    var value = _ref2.value,
      _ref2$children = _ref2.children,
      children = _ref2$children === undefined ? [] : _ref2$children,
      handlerProps = _objectWithoutProperties(_ref2, ['value', 'children']);
    if ((R.isNil(value) || R.isEmpty(value)) && R.isEmpty(children)) {
      return R.prop('noValue', labels);
    }
    if (R.is(Function, valueHandler)) {
      return valueHandler(_extends({
        value: value
      }, handlerProps));
    }
    return value;
  };
  var isAllOpen = depthLevel >= defaultDepthLevel;
  var buttonAllOpenLabel = isAllOpen ? R.prop('collapseAll', labels) : R.prop('expandAll', labels);
  return React.createElement(Fragment, null, React.createElement(Container, {
    disableGutters: true,
    'data-testid': testId,
    className: classes.container
  }, React.createElement(Button, {
    'aria-label': buttonAllOpenLabel,
    color: 'primary',
    endIcon: isAllOpen ? React.createElement(KeyboardArrowUpIcon, {
      color: 'primary'
    }) : React.createElement(KeyboardArrowDownIcon, {
      color: 'primary'
    }),
    onClick: function onClick() {
      return handleCollapse(isAllOpen);
    }
  }, buttonAllOpenLabel), React.createElement('div', null, children, React.createElement(IconButton, {
    color: 'primary',
    onClick: onClick
  }, icon))), R.map(function (_ref3) {
    var id = _ref3.id,
      label = _ref3.label,
      _ref3$children = _ref3.children,
      children = _ref3$children === undefined ? [] : _ref3$children;
    return React.createElement(Fragment, {
      key: id
    }, React.createElement(Divider, {
      className: classes.divider
    }), React.createElement(Typography, {
      variant: 'h5',
      className: classes.title
    }, label), R.map(function (_ref4) {
      var id = _ref4.id,
        label = _ref4.label,
        children = _ref4.children,
        value = _ref4.value,
        _ref4$handlerProps = _ref4.handlerProps,
        handlerProps = _ref4$handlerProps === undefined ? {} : _ref4$handlerProps;
      return React.createElement(Collapse, {
        key: id,
        level: 0,
        label: label,
        isOpen: depthLevel >= 0
      }, React.createElement(Typography, {
        className: cx(classes.spacingValue, classes.overflow),
        variant: 'body2'
      }, getValue(_extends({
        value: value,
        children: children
      }, handlerProps))), children && R.map(function (_ref5) {
        var id = _ref5.id,
          label = _ref5.label,
          value = _ref5.value,
          _ref5$handlerProps = _ref5.handlerProps,
          handlerProps = _ref5$handlerProps === undefined ? {} : _ref5$handlerProps;
        return React.createElement(Collapse, {
          key: id,
          level: 1,
          variant: VARIANT_BOLD,
          label: label,
          isOpen: depthLevel >= 1
        }, React.createElement(Typography, {
          variant: 'body2',
          className: classes.overflow
        }, getValue(_extends({
          value: value
        }, handlerProps))));
      }, children));
    }, children));
  }, data));
};
CollapsibleTree.propTypes = process.env.NODE_ENV !== "production" ? {
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string,
      children: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string,
        value: PropTypes.string
      }))
    }))
  })),
  defaultDepthLevel: PropTypes.number,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
  labels: PropTypes.shape({
    collapseAll: PropTypes.string,
    expandAll: PropTypes.string,
    noValue: PropTypes.string
  }),
  testId: PropTypes.string,
  valueHandler: PropTypes.func,
  icon: PropTypes.element,
  onClick: PropTypes.func
} : {};
export default CollapsibleTree;